import React, { useEffect, useState } from "react";
import "../assets/EndRemoteDebug.css";
import "../assets/AllTabsCommon.css";
import "../assets/ViewRemoteDebugSession.css"; // Import the new CSS file
import { fetchData, postData } from "../api/restapi";
import Popup from "../popups/popup";
import { CHATBOT_RESTRICTED_GROUPS } from "../../config/featureAccess";

import Select from "react-select"; // Import Select component for dropdown

function ViewRemoteDebugSessionTab({ userInfo, role, userGroups = [] }) {
  const isRecommendationsRestricted = userGroups.some(g => CHATBOT_RESTRICTED_GROUPS.includes(g));
  const [activeSessions, setActiveSessions] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedRowData, setExpandedRowData] = useState(null);
  const [expandedAutoCrashRowId, setExpandedAutoCrashRowId] = useState(null);
  const [selectedFileFilter, setSelectedFileFilter] = useState({});
  const [autoCrashData, setAutoCrashData] = useState({});
  const [autoCrashEventSources, setAutoCrashEventSources] = useState({});
  const [autoCrashConnections, setAutoCrashConnections] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [modalErrorMessage, setModalErrorMessage] = useState(""); // New state for modal-specific errors
  // Search state
  const [systemIdSearchTerm, setSystemIdSearchTerm] = useState("");
  const [sessionIdSearchTerm, setSessionIdSearchTerm] = useState("");
  const [diagnosticData, setDiagnosticData] = useState([]);
  const [uploadStats, setUploadStats] = useState({ filesUploadedCount: 0, uploadBaseUrl: "" });
  const [collectorNames, setCollectorNames] = useState({});
  const [groupedDiagnostic, setGroupedDiagnostic] = useState({});
  const [maxUpdateCounter, setMaxUpdateCounter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // Configurable value
  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 1,
    hasNext: false,
    hasPrevious: false
  });
  
  // State for tracking expanded rules cells: { 'diagnosticName-columnType': true/false }
  const [expandedRulesCells, setExpandedRulesCells] = useState({});

  // State for config file modal
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [modalConfigData, setModalConfigData] = useState(null);
  const [currentModalSessionId, setCurrentModalSessionId] = useState("");
  const [currentModalCollectorLabel, setCurrentModalCollectorLabel] = useState("");
  const [collectorOptions, setCollectorOptions] = useState([]); // For diagnostic data dropdown

  const tableColumns = [
    { key: "systemId", label: "System ID" },
    { key: "sessionId", label: "Session ID" },
    { key: "sessionEndedAt", label: "Session Ended At", tooltip: "All times are displayed in your local time zone." },
    { key: "status", label: "Status" },
    { key: "user", label: "Created User" },
    ...(!isRecommendationsRestricted ? [{ key: "autoCrash", label: "Recommendations", isAction: true }] : []),
    { key: "configFile", label: "Config File", isAction: true }
  ];

  // Transform API data to UI format
  const transformAutoCrashData = (apiResponse) => {
    return {
      filesUploaded: apiResponse.stats.filesUploaded,
      filesProcessed: apiResponse.stats.filesProcessed,
      files: apiResponse.processedFiles.map(file => ({
        name: file.filename,
        status: "Completed",
        timestamp: new Date().toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        }),
        problem: {
          crashArea: file.problem.crashArea,
          errorSignature: file.problem.errorSignature,
          softwarePl: file.problem.softwarePl,
          error: file.problem.crashError
        },
        fixes: file.cr_matched_data.map(cr => ({
          cr: cr.changeRequestId,
          crTitle: cr.changeRequestTitle,
          reportedBuild: cr.reportedBuild,
          dateFound: cr.dateIssueFound,
          buildFixed: cr.fixedBuild,
          foundOnImage: cr.matchedImage,
          foundOnProduct: cr.matchedProduct
        }))
      }))
    };
  };

  // Extract unique CRs with frequency tracking
  const extractUniqueCRsWithFrequency = (files) => {
    const crMap = new Map();
    
    files.forEach(file => {
      // Track which CRs appear in this file (use Set to avoid duplicates per file)
      const crsInThisFile = new Set();
      
      file.fixes.forEach(fix => {
        crsInThisFile.add(fix.cr);
        
        if (crMap.has(fix.cr)) {
          crMap.get(fix.cr).totalRecommendations++;
        } else {
          crMap.set(fix.cr, {
            id: fix.cr,
            title: fix.crTitle,
            fileCount: 0,
            totalRecommendations: 1
          });
        }
      });
      
      // Increment file count for each unique CR in this file
      crsInThisFile.forEach(crId => {
        crMap.get(crId).fileCount++;
      });
    });
    
    // Sort by file count first, then by total recommendations
    return Array.from(crMap.values()).sort((a, b) => {
      if (b.fileCount !== a.fileCount) {
        return b.fileCount - a.fileCount;
      }
      return b.totalRecommendations - a.totalRecommendations;
    });
  };

  // Setup SSE connection for Auto Crash data
  const setupAutoCrashSSE = (sessionId) => {
    try {
      const sseUrl = `${process.env.REACT_APP_WBC_API_BASE_URL}/api/auto-crash/session-stream?sessionId=${sessionId}`;
      console.log('Auto Crash SSE: Connecting to', sseUrl);
      
      const es = new EventSource(sseUrl);
      
      es.onopen = () => {
        console.log('Auto Crash SSE connection opened for session:', sessionId);
        setAutoCrashConnections(prev => ({
          ...prev,
          [sessionId]: true
        }));
      };
      
      // Handle initial data load
      es.addEventListener('auto-crash-data', (event) => {
        try {
          const apiData = JSON.parse(event.data);
          const transformedData = transformAutoCrashData(apiData);
          setAutoCrashData(prev => ({
            ...prev,
            [sessionId]: transformedData  // Use original sessionId with prefix
          }));
          console.log('Auto Crash SSE: Initial data received for session:', sessionId);
        } catch (err) {
          console.error('Auto Crash SSE: Error parsing initial data:', err);
        }
      });
      
      // Handle incremental updates
      es.addEventListener('auto-crash-update', (event) => {
        try {
          const apiData = JSON.parse(event.data);
          const transformedData = transformAutoCrashData(apiData);
          setAutoCrashData(prev => ({
            ...prev,
            [sessionId]: transformedData  // Use original sessionId with prefix
          }));
          console.log('Auto Crash SSE: Update received for session:', sessionId);
        } catch (err) {
          console.error('Auto Crash SSE: Error parsing update data:', err);
        }
      });
      
      es.onerror = (error) => {
        console.log('Auto Crash SSE connection event for session:', sessionId, 'readyState:', error.target.readyState);
        setAutoCrashConnections(prev => ({
          ...prev,
          [sessionId]: false
        }));
        
        if (error.target.readyState === EventSource.CLOSED) {
          console.log('Auto Crash SSE connection closed for session:', sessionId);
        } else if (error.target.readyState === EventSource.CONNECTING) {
          console.log('Auto Crash SSE reconnecting for session:', sessionId);
        }
      };
      
      setAutoCrashEventSources(prev => ({
        ...prev,
        [sessionId]: es
      }));
      
    } catch (err) {
      console.error('Failed to setup Auto Crash SSE connection:', err);
      setAutoCrashConnections(prev => ({
        ...prev,
        [sessionId]: false
      }));
    }
  };

  // Cleanup SSE connections on unmount
  useEffect(() => {
    return () => {
      Object.values(autoCrashEventSources).forEach(es => {
        if (es) {
          es.close();
        }
      });
    };
  }, [autoCrashEventSources]);

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on new search
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [systemIdSearchTerm, sessionIdSearchTerm]);

  useEffect(() => {
    const fetchActiveSessions = async () => {
      try {
        // Build query parameters
        let queryParams = `user=${userInfo}&sessionState=INACTIVE&userRole=${role}&page=${currentPage}&size=${rowsPerPage}`;
        
        // Add search filters if provided
        if (systemIdSearchTerm) {
          queryParams += `&systemId=${encodeURIComponent(systemIdSearchTerm)}`;
        }
        if (sessionIdSearchTerm) {
          queryParams += `&sessionId=${encodeURIComponent(sessionIdSearchTerm)}`;
        }
        
        const data = await fetchData(`/api/v1/diagnostic-sessions?${queryParams}`);
        if (data.statusCode === 200) {
          const sessionsWithActiveUntil = data.activeRemoteDebugSessions.map(session => {
            const activeUntilDate = new Date(new Date(session.createdOn).getTime() + session.sessionTimeInMins * 60000);
            const formattedActiveUntil = activeUntilDate.toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false
            }).replace(",", "");
            
            // Format sessionEndedAt for display in table
            const formattedSessionEndedAt = session.sessionEndedAt 
              ? new Date(session.sessionEndedAt).toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false
                }).replace(",", "")
              : "N/A";
            
            return {
              ...session,
              activeUntil: formattedActiveUntil,
              activeUntilDate: activeUntilDate,
              sessionEndedAt: formattedSessionEndedAt
            };
          });
          
          setActiveSessions(sessionsWithActiveUntil);
          
          // Set pagination info from backend response
          if (data.pagination) {
            setPaginationInfo({
              totalElements: data.pagination.totalElements,
              totalPages: data.pagination.totalPages,
              currentPage: data.pagination.currentPage,
              hasNext: data.pagination.hasNext,
              hasPrevious: data.pagination.hasPrevious
            });
          }
        }
      } catch (err) {
        console.error("Error fetching active sessions:", err);
      }
    };

    const fetchCollectorNames = async () => {
      try {
        const data = await fetchData("/api/v1/diagnostic-data");
        if (data.statusCode === 200) {
          const namesMap = data.collectorIds.reduce((acc, collector) => {
            acc[collector.id] = collector.name;
            return acc;
          }, {});
          setCollectorNames(namesMap);
          // Also set options for the dropdown
          const options = data.collectorIds.map(collector => ({
            value: collector.id,
            label: collector.name
          }));
          setCollectorOptions(options);
        }
      } catch (err) {
        console.error("Error fetching collector names:", err);
      }
    };

    fetchActiveSessions();
    fetchCollectorNames();
  }, [userInfo, role, currentPage, systemIdSearchTerm, sessionIdSearchTerm, rowsPerPage]);

  // Build grouped diagnostic commands by collector and update counter
  useEffect(() => {
    const map = {};
    let maxCounter = 0;
    diagnosticData.forEach(d => {
      const label = collectorNames[d.collectorId] || d.collectorId;
      if (!map[label]) {
        map[label] = { created: { command: '-', logMask: null, metadata: null } };
      }
      if (d.collectorUpdateCounter === 0) {
        map[label].created = {
          command: d.mqttCommandName,
          logMask: d.logMaskDescription || null,
          metadata: d.metadata || null
        };
      } else {
        const key = `update${d.collectorUpdateCounter}`;
        map[label][key] = {
          command: d.mqttCommandName,
          logMask: d.logMaskDescription || null,
          metadata: d.metadata || null
        };
      }
      if (d.collectorUpdateCounter > maxCounter) {
        maxCounter = d.collectorUpdateCounter;
      }
    });
    setGroupedDiagnostic(map);
    setMaxUpdateCounter(maxCounter);
  }, [diagnosticData, collectorNames]);

  const handleFetchConfigFile = async (sessionId, collectorId, collectorLabel) => {
    setErrorMessage(""); // Clear any general popup errors
    setModalErrorMessage(""); // Clear previous modal errors
    setModalConfigData(null); // Clear previous config data
    setCurrentModalSessionId(sessionId); // Set session ID immediately for display
    setCurrentModalCollectorLabel(collectorLabel); // Set collector label immediately for display
    setShowConfigModal(true); // Always open modal to show loading or error

    try {
      const response = await fetchData(`/api/v1/rd-config-json?sessionId=${sessionId}&collectorId=${collectorId}`);
      if (response.statusCode === 400) {
        setModalErrorMessage(response.errorMessage || "Failed to generate config file.");
      } else {
        setModalConfigData(response);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
      setModalErrorMessage("Failed to generate config file due to network error.");
    }
  };

  const handleCopyToClipboard = async () => {
    if (modalConfigData) {
      try {
        const jsonString = JSON.stringify(modalConfigData, null, 2);
        await navigator.clipboard.writeText(jsonString);
        alert('JSON copied to clipboard!'); // Temporary feedback
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        alert('Failed to copy to clipboard');
      }
    }
  };

  const handleDownload = () => {
    if (modalConfigData) {
      const dataStr = JSON.stringify(modalConfigData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `${currentModalCollectorLabel}_${currentModalSessionId}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }
  };

  const handleAutoCrashToggle = (session) => {
    // If clicking on the same row, collapse it
    if (expandedAutoCrashRowId === session.sessionId) {
      setExpandedAutoCrashRowId(null);
      // Reset file filter for this session
      setSelectedFileFilter(prev => {
        const updated = { ...prev };
        delete updated[session.sessionId];
        return updated;
      });
      // Close SSE connection for this session
      const es = autoCrashEventSources[session.sessionId];
      if (es) {
        es.close();
        setAutoCrashEventSources(prev => {
          const updated = { ...prev };
          delete updated[session.sessionId];
          return updated;
        });
      }
      // Clear auto crash data
      setAutoCrashData(prev => {
        const updated = { ...prev };
        delete updated[session.sessionId];
        return updated;
      });
      // Clear connection status
      setAutoCrashConnections(prev => {
        const updated = { ...prev };
        delete updated[session.sessionId];
        return updated;
      });
      return;
    }

    // Collapse any open session details
    setExpandedRowId(null);
    setExpandedRowData(null);

    // Expand the auto crash details for this row
    setExpandedAutoCrashRowId(session.sessionId);
    // Initialize file filter to null (no selection) for this session
    setSelectedFileFilter(prev => ({
      ...prev,
      [session.sessionId]: null
    }));
    // Setup SSE connection for this session
    setupAutoCrashSSE(session.sessionId);
  };

  const handleFileFilterChange = (sessionId, fileName) => {
    setSelectedFileFilter(prev => ({
      ...prev,
      [sessionId]: fileName
    }));
  };

  const handleViewDetails = async (session) => {
    // If clicking on the same row, collapse it
    if (expandedRowId === session.sessionId) {
      setExpandedRowId(null);
      setExpandedRowData(null);
      return;
    }

    // Collapse any open auto crash details
    if (expandedAutoCrashRowId) {
      const es = autoCrashEventSources[expandedAutoCrashRowId];
      if (es) {
        es.close();
      }
    }
    setExpandedAutoCrashRowId(null);

    // Expand the new row
    setDiagnosticData(session.collectorExecutionStatuses);
    try {
      const response = await fetchData(`/api/v1/diagnostic-data-upload-stats?sessionId=${session.sessionId}`);
      if (response.statusCode === 200) {
        setUploadStats(response.fileUploadStats);
        setExpandedRowId(session.sessionId);
        setExpandedRowData({
          ...session,
          uploadStats: response.fileUploadStats
        });
      } else {
        setErrorMessage(response.errorMessage + " Failed to fetch upload stats.");
      }
    } catch (error) {
      setErrorMessage(`Failed to fetch upload stats: ${error.message}`);
    }
  };

  const closePopup = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  const closeConfigModal = () => {
    setShowConfigModal(false);
    setModalConfigData(null);
    setCurrentModalSessionId("");
    setCurrentModalCollectorLabel("");
    setModalErrorMessage(""); // Clear modal error message on close

    // Clear the selected collector for the specific session that was active in the modal
    setActiveSessions(prevSessions => prevSessions.map(session =>
      session.sessionId === currentModalSessionId ? { ...session, selectedCollector: null } : session
    ));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Toggle function for expanding/collapsing rules in a specific cell
  const toggleRulesCell = (diagnosticName, columnType) => {
    const cellKey = `${diagnosticName}-${columnType}`;
    setExpandedRulesCells(prev => ({
      ...prev,
      [cellKey]: !prev[cellKey]
    }));
  };

  // Helper function to render command cell with collapsible rules
  const renderCommandCell = (diagnosticName, commandData, columnType) => {
    if (!commandData) return '-';
    
    const cellKey = `${diagnosticName}-${columnType}`;
    const isExpanded = expandedRulesCells[cellKey];
    const hasRules = commandData.metadata?.odlMode === "ODL_CONTEXT_COLLECTION" && 
                     commandData.metadata.rules && 
                     commandData.metadata.rules.length > 0;
    
    return (
      <div className="command-cell-content">
        <span className="command-name">{commandData.command}</span>
        {hasRules ? (
          <div style={{ marginTop: '6px' }}>
            <button
              onClick={() => toggleRulesCell(diagnosticName, columnType)}
              style={{
                background: 'none',
                border: 'none',
                color: '#1a4fba',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 500
              }}
            >
              {commandData.metadata.rules.length} Rule{commandData.metadata.rules.length !== 1 ? 's' : ''}
              <span style={{ fontSize: '10px' }}>{isExpanded ? '▲' : '▼'}</span>
            </button>
            {isExpanded && (
              <div style={{ marginTop: '6px', marginLeft: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {commandData.metadata.rules.map((rule, ri) => (
                  <div key={ri} style={{ fontSize: '12px', background: '#f5f7fa', border: '1px solid #e0e4ea', borderRadius: '5px', padding: '4px 8px', display: 'flex', gap: '6px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#888', fontWeight: 500 }}>Rule:</span>
                    <span style={{ color: '#333', fontWeight: 500 }}>{rule.ruleName || `Rule ${rule.ruleId}`}</span>
                    {rule.logMaskDescription && (
                      <>
                        <span style={{ color: '#ccc' }}>|</span>
                        <span style={{ color: '#888', fontWeight: 500 }}>Log Mask:</span>
                        <span style={{ color: '#555' }}>{rule.logMaskDescription}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : commandData.metadata?.odlMode === "ODL_CONTEXT_COLLECTION" ? (
          <span style={{ fontSize: '12px', color: '#999', marginLeft: '4px' }}>(no rules)</span>
        ) : commandData.logMask ? (
          <>
            <span className="log-mask-separator"> | </span>
            <span className="log-mask-description">Log Mask: {commandData.logMask}</span>
          </>
        ) : null}
      </div>
    );
  };

  const renderPageNumbers = () => {
    const totalPages = paginationInfo.totalPages;
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    return (
      <>
        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>&lt;&lt;</button>
        {pageNumbers}
        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>&gt;&gt;</button>
      </>
    );
  };

  return (
    <div className="data-table-tab-container">
      <div className="tab-heading">
        <h1>All Completed Sessions</h1>
      </div>
      <div className="search-container">
        <input
          type="text"
          placeholder="🔍 Search by System ID..."
          value={systemIdSearchTerm}
          onChange={(e) => setSystemIdSearchTerm(e.target.value)}
        />
        <input
          type="text"
          placeholder="🔍 Search by Session ID..."
          value={sessionIdSearchTerm}
          onChange={(e) => setSessionIdSearchTerm(e.target.value)}
        />
      </div>
      <table className="data-table">
        <thead>
          <tr>
            {tableColumns.map((col) => (
              <th key={col.key}>
                {col.label}
                {col.tooltip && (
                  <span className="tooltip-icon" data-tooltip={col.tooltip}>ℹ️</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {activeSessions.map((session) => (
            <React.Fragment key={session.sessionId}>
              <tr className={expandedRowId === session.sessionId ? 'expanded-row' : ''}>
                {tableColumns.map((col) => {
                  if (col.key === "configFile") {
                    return (
                      <td key={col.key}>
                        <Select
                          className="dropdown multi-select"
                          options={collectorOptions}
                          onChange={(selectedOption) => {
                            // Update the selected collector for this specific session in the activeSessions state
                            setActiveSessions(prevSessions =>
                              prevSessions.map(s =>
                                s.sessionId === session.sessionId ? { ...s, selectedCollector: selectedOption } : s
                              )
                            );
                            handleFetchConfigFile(session.sessionId, selectedOption.value, selectedOption.label);
                          }}
                          value={session.selectedCollector || null} // Control the value of the Select component
                          placeholder="Diagnostic Data"
                        />
                      </td>
                    );
                  }
                  
                  if (col.key === "autoCrash") {
                    return (
                      <td key={col.key}>
                        <span 
                          className="auto-crash-link" 
                          onClick={() => handleAutoCrashToggle(session)}
                        >
                          {expandedAutoCrashRowId === session.sessionId ? "Hide Details" : "View Details"}
                        </span>
                      </td>
                    );
                  }
                  
                  let cellValue = session[col.key] || "";

                  if (col.key === "sessionId") {
                    return (
                      <td key={col.key} onClick={() => handleViewDetails(session)} className="clickable-session-id">
                        {cellValue}
                      </td>
                    );
                  }
                  return <td key={col.key}>{session[col.key] || ""}</td>;
                })}
              </tr>
              {expandedAutoCrashRowId === session.sessionId && (() => {
                const sessionAutoCrashData = autoCrashData[session.sessionId];
                const isConnected = autoCrashConnections[session.sessionId];
                const currentFiles = sessionAutoCrashData?.files || [];
                const uniqueCRs = extractUniqueCRsWithFrequency(currentFiles);
                
                return (
                <tr className="auto-crash-details-row">
                  <td colSpan={tableColumns.length}>
                    <div className="auto-crash-container">
                      {/* Session ID Banner */}
                      <div className="transaction-banner">
                        <div className="transaction-id">
                          <i className="material-icons">fingerprint</i>
                          Session ID: {session.sessionId}
                        </div>
                        {isConnected && (
                          <div className="live-indicator">
                            <div className="pulse"></div>
                            Live Updates
                          </div>
                        )}
                      </div>
                      
                      {/* Stats Bar */}
                      <div className="stats-bar">
                        <div className="stat-block files-uploaded">
                          <i className="material-icons stat-icon">file_upload</i>
                          <div className="stat-number">{sessionAutoCrashData?.filesUploaded || 0}</div>
                          <div className="stat-label">
                            Files Uploaded
                            <span className="tooltip-icon" data-tooltip="Minidump file uploaded">ℹ️</span>
                          </div>
                        </div>
                        <div className="stat-block files-processed">
                          <i className="material-icons stat-icon">check_circle</i>
                          <div className="stat-number">{sessionAutoCrashData?.filesProcessed || 0}</div>
                          <div className="stat-label">Files Processed</div>
                        </div>
                      </div>
                      
                      {/* Recommended CR Fixes Section */}
                      <div className="recommended-cr-section">
                        <div className="cr-section-header">
                          <i className="material-icons">build</i>
                          <span>Recommended CR Fixes</span>
                          <span className="cr-count">({uniqueCRs.length} unique CR{uniqueCRs.length !== 1 ? 's' : ''})</span>
                        </div>
                        <div className="cr-list">
                          {uniqueCRs.length > 0 ? (
                            uniqueCRs.map((cr, idx) => (
                              <div key={idx} className="cr-item">
                                <span className="cr-id">{cr.id}</span>
                                <span className="cr-separator">-</span>
                                <span className="cr-title">{cr.title}</span>
                                {cr.fileCount > 1 || cr.totalRecommendations > cr.fileCount ? (
                                  <span className="cr-frequency">
                                    ({cr.fileCount} {cr.fileCount === 1 ? 'file' : 'files'}, {cr.totalRecommendations} {cr.totalRecommendations === 1 ? 'recommendation' : 'recommendations'})
                                  </span>
                                ) : null}
                              </div>
                            ))
                          ) : (
                            <div className="cr-empty-state">
                              No CR recommendations available yet
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* File Results Section */}
                      <div className="file-results">
                        <div className="file-controls">
                          <div className="filter-controls">
                            <select 
                              className="filter-select"
                              value={selectedFileFilter[session.sessionId] || ""}
                              onChange={(e) => handleFileFilterChange(session.sessionId, e.target.value || null)}
                            >
                              <option value="">No File</option>
                              <option value="All Files">All Files</option>
                              {currentFiles.map((file, idx) => (
                                <option key={idx} value={file.name}>{file.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {/* File Cards - Show only when a selection is made */}
                        {currentFiles.length === 0 ? (
                          sessionAutoCrashData?.filesUploaded > 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                              <i className="material-icons" style={{ fontSize: '48px', marginBottom: '10px' }}>hourglass_empty</i>
                              <p>Waiting for files to be processed...</p>
                            </div>
                          ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                              <i className="material-icons" style={{ fontSize: '48px', marginBottom: '10px' }}>cloud_off</i>
                              <p>No files uploaded</p>
                            </div>
                          )
                        ) : !selectedFileFilter[session.sessionId] ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                            <i className="material-icons" style={{ fontSize: '48px', marginBottom: '10px' }}>folder_open</i>
                            <p>Please select a file from the dropdown to view details</p>
                          </div>
                        ) : (
                          currentFiles
                            .filter(file => {
                              const currentFilter = selectedFileFilter[session.sessionId] || "All Files";
                              return currentFilter === "All Files" || file.name === currentFilter;
                            })
                            .map((file, fileIdx) => (
                          <div key={fileIdx} className="file-card">
                            <div className="file-header">
                              <div className="file-name">
                                <i className="material-icons">description</i>
                                {file.name}
                              </div>
                              <div className="file-status">
                                <span className={`status-badge ${file.status === 'Completed' ? 'status-completed' : 'status-processing'}`}>
                                  {file.status}
                                </span>
                              </div>
                            </div>
                            <div className="file-content">
                              {/* Problem Section */}
                              <div className="crash-section problem">
                                <div className="crash-section-header problem">
                                  <i className="material-icons">report_problem</i>
                                  Problem
                                </div>
                                <div className="crash-section-content">
                                  <div className="crash-details">
                                    <div className="crash-item">
                                      <span className="crash-label">Crash Area:</span>
                                      <span className="crash-value">{file.problem.crashArea}</span>
                                    </div>
                                    <div className="crash-item">
                                      <span className="crash-label">Error Signature:</span>
                                      <span className="crash-value">{file.problem.errorSignature}</span>
                                    </div>
                                    <div className="crash-item">
                                      <span className="crash-label">Software PL:</span>
                                      <span className="crash-value">{file.problem.softwarePl}</span>
                                    </div>
                                    <div className="crash-item">
                                      <span className="crash-label">Error:</span>
                                      <span className="crash-value">{file.problem.error}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Recommended Fix Section */}
                              <div className="crash-section fix">
                                <div className="crash-section-header fix">
                                  <i className="material-icons">check_circle</i>
                                  Recommended Fix
                                </div>
                                <div className="crash-section-content">
                                  {file.fixes.map((fix, fixIdx) => (
                                    <div key={fixIdx} className="cr-match">
                                      <div className="crash-item">
                                        <span className="crash-label">CR:</span>
                                        <span className="crash-value">{fix.cr}</span>
                                      </div>
                                      <div className="crash-item">
                                        <span className="crash-label">CR Title:</span>
                                        <span className="crash-value">{fix.crTitle}</span>
                                      </div>
                                      <div className="crash-item">
                                        <span className="crash-label">Reported Build:</span>
                                        <span className="crash-value">{fix.reportedBuild}</span>
                                      </div>
                                      <div className="crash-item">
                                        <span className="crash-label">Date Found:</span>
                                        <span className="crash-value">{fix.dateFound}</span>
                                      </div>
                                      <div className="crash-item">
                                        <span className="crash-label">Build Fixed:</span>
                                        <span className="crash-value">{fix.buildFixed}</span>
                                      </div>
                                      <div className="crash-item">
                                        <span className="crash-label">Found On Image:</span>
                                        <span className="crash-value">{fix.foundOnImage}</span>
                                      </div>
                                      <div className="crash-item">
                                        <span className="crash-label">Found On Product:</span>
                                        <span className="crash-value">{fix.foundOnProduct}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                          ))
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
                );
              })()}
              {expandedRowId === session.sessionId && expandedRowData && (
                <tr className="expanded-details-row">
                  <td colSpan={tableColumns.length}>
                    <div className="expanded-row-content">
                      {/* Row 1: Session Information and Diagnostic Config */}
                      <div className="expanded-details-cards expanded-details-row-1">
                        <div className="expanded-detail-card">
                          <h4>
                            <span className="card-header-icon info-icon"></span>
                            Session Information
                          </h4>
                          <div className="expanded-detail-card-content">
                            <div className="session-info-item">
                              <span className="info-label">Open Ended:</span>
                              <span className="info-value">{expandedRowData.isOpenEnded ? "Yes" : "No"}</span>
                            </div>
                            {!expandedRowData.isOpenEnded && (
                              <div className="session-info-item">
                                <span className="info-label">Duration:</span>
                                <span className="info-value">{expandedRowData.sessionTimeInMins} mins</span>
                              </div>
                            )}
                            <div className="session-info-item">
                              <span className="info-label">Session Started At:</span>
                              <span className="info-value">{expandedRowData.sessionStartTime ? new Date(expandedRowData.sessionStartTime).toLocaleString(undefined, {year:"numeric", month:"2-digit", day:"2-digit", hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:false}).replace(",", "") : "N/A"}</span>
                            </div>
                            <div className="session-info-item">
                              <span className="info-label">Session End Time:</span>
                              <span className="info-value">{expandedRowData.activeUntil}</span>
                            </div>
                            <div className="session-info-item">
                              <span className="info-label">Termination Reason:</span>
                              <span className="info-value">{expandedRowData.reason || "Session ended as scheduled"}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="expanded-detail-card">
                          <h4>
                            <span className="card-header-icon diagnostic-icon"></span>
                            Diagnostic Config
                          </h4>
                          <div className="expanded-detail-card-content">
                            <div className="diagnostic-table-wrapper">
                            <table className="details-table">
                              <thead>
                                <tr>
                                  <th>Diagnostic Data</th>
                                  <th>Command – Created</th>
                                  {Array.from({ length: maxUpdateCounter }).map((_, i) => {
                                    const ordinals = ['First','Second','Third','Fourth','Fifth','Sixth','Seventh','Eighth','Ninth','Tenth'];
                                    const label = ordinals[i] || `${i+1}th`;
                                    return <th key={i}>Command – {label} Update</th>;
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(groupedDiagnostic).map(([diag, cmds]) => (
                                  <tr key={diag}>
                                    <td>{diag}</td>
                                    <td>{renderCommandCell(diag, cmds.created, 'created')}</td>
                                    {Array.from({ length: maxUpdateCounter }).map((_, i) => {
                                      const updateData = cmds[`update${i + 1}`];
                                      return (
                                        <td key={i}>
                                          {renderCommandCell(diag, updateData, `update${i + 1}`)}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Upload Statistics (Full Width) */}
                      <div className="expanded-details-cards expanded-details-row-2">
                        <div className="expanded-detail-card expanded-detail-card-full-width">
                          <h4>
                            <span className="card-header-icon stats-icon"></span>
                            Upload Statistics
                          </h4>
                          <div className="expanded-detail-card-content">
                            <div className="upload-stats-summary">
                              <div className="stats-summary-item">
                                <span className="stats-label">Files Uploaded Count:</span>
                                <span className="upload-stats-value">{expandedRowData.uploadStats?.filesUploadedCount ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="pagination-controls">{renderPageNumbers()}</div>
      {successMessage && (
        <Popup message={successMessage} onClose={closePopup} />
      )}
      {errorMessage && (
        <Popup message={errorMessage} onClose={closePopup} />
      )}
      
      {showConfigModal && (
        <div className="config-modal-overlay">
          <div className="config-modal-sidebar open-from-right">
            <div className="config-modal-header">
              <h2>Config File for {currentModalSessionId}</h2>
              <button className="close-button" onClick={closeConfigModal}>&times;</button>
            </div>
            <div className="config-modal-content">
              {modalErrorMessage ? (
                <div className="modal-error-message">{modalErrorMessage}</div>
              ) : modalConfigData ? (
                <>
                  <h4>Generated Configuration:</h4>
                  <div className="json-container">
                    <pre className="json-content">
                      {JSON.stringify(modalConfigData, null, 2)}
                    </pre>
                  </div>
                  <div className="config-actions">
                    <button className="go-back-modal-button" onClick={closeConfigModal}>
                      &lt; Back
                    </button>
                    <button className="submit-button" onClick={handleCopyToClipboard}>
                      Copy to Clipboard
                    </button>
                    <button className="submit-button" onClick={handleDownload}>
                      Download File
                    </button>
                  </div>
                </>
              ) : (
                <p>Loading configuration data...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewRemoteDebugSessionTab;
